import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowPathIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline'
import AccountingToolPage from '@/components/AccountingToolPage'
import { toast } from '@/store/toastStore'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import SortableHeader from '@/components/SortableHeader'
import { GroupBySelect, HoverHelp, TableToolbar } from '@/tableControls'
import { usePageAccess } from '@/hooks/usePageAccess'
import { useSortableTable } from '@/hooks/useSortableTable'
import {
  createEmployeeLocation,
  deleteEmployeeLocation,
  getEmployeeLocations,
  importEmployeeLocationsCsv,
  updateEmployeeLocation,
  UHC_HOME_STORES,
  type EmployeeLocation,
  type EmployeeLocationCreate,
} from '@/services/uhcAllocations'
import { formatApiError } from '@/utils/apiError'
import UhcAllocate from '@/pages/uhc/UhcAllocate'
import {
  inputClassName,
  isFieldEmpty,
  locationRowHasMissingFields,
} from '@/pages/uhcAuditDisplay'

type TabId = 'allocation' | 'locations'

const storeOptions: SelectOption[] = UHC_HOME_STORES.map((s) => ({ id: s, label: s }))

type LocationGroupBy = 'none' | 'uhc_subscriber_name' | 'homebase_employee_id' | 'home_store'

interface DraftLocationRow {
  draftId: string
  anchorId: number | null
  uhc_subscriber_name: string
  homebase_employee_id: string
  homebase_first_name: string
  homebase_last_name: string
  home_store: string
}

type LocationDisplayItem =
  | { kind: 'location'; row: EmployeeLocation }
  | { kind: 'draft'; draft: DraftLocationRow }

const LOCATION_GROUP_OPTIONS: { value: LocationGroupBy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'uhc_subscriber_name', label: 'Subscriber' },
  { value: 'homebase_employee_id', label: 'Homebase ID' },
  { value: 'home_store', label: 'Home store' },
]

function emptyDraftRow(anchorId: number | null, homeStore: string): DraftLocationRow {
  return {
    draftId: crypto.randomUUID(),
    anchorId,
    uhc_subscriber_name: '',
    homebase_employee_id: '',
    homebase_first_name: '',
    homebase_last_name: '',
    home_store: homeStore,
  }
}

function mergeLocationsWithDrafts(
  sortedLocations: EmployeeLocation[],
  drafts: DraftLocationRow[]
): LocationDisplayItem[] {
  const items: LocationDisplayItem[] = []
  for (const draft of drafts.filter((d) => d.anchorId === null)) {
    items.push({ kind: 'draft', draft })
  }
  for (const row of sortedLocations) {
    items.push({ kind: 'location', row })
    for (const draft of drafts.filter((d) => d.anchorId === row.id)) {
      items.push({ kind: 'draft', draft })
    }
  }
  return items
}

function locationGroupKey(item: LocationDisplayItem, groupBy: LocationGroupBy): string {
  if (groupBy === 'none') return ''
  const source = item.kind === 'location' ? item.row : item.draft
  const value = String(source[groupBy] ?? '').trim()
  if (groupBy === 'uhc_subscriber_name') return value || '(No subscriber)'
  if (groupBy === 'homebase_employee_id') return value || '(No ID)'
  return value || '(No store)'
}

function buildLocationSections(
  items: LocationDisplayItem[],
  groupBy: LocationGroupBy,
  sortKey?: string,
  sortDirection?: 'asc' | 'desc'
): Array<{ label: string; items: LocationDisplayItem[] }> {
  const sections =
    groupBy === 'none'
      ? [{ label: '', items }]
      : (() => {
          const groups = new Map<string, LocationDisplayItem[]>()
          for (const item of items) {
            const key = locationGroupKey(item, groupBy)
            const bucket = groups.get(key) ?? []
            bucket.push(item)
            groups.set(key, bucket)
          }
          return [...groups.entries()]
            .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map(([label, groupItems]) => ({ label, items: groupItems }))
        })()

  if (!sortKey || !sortDirection) return sections

  return sections.map((section) => ({
    ...section,
    items: [...section.items].sort((a, b) => {
      const read = (item: LocationDisplayItem) => {
        const source = item.kind === 'location' ? item.row : item.draft
        const value = source[sortKey as keyof typeof source]
        return value == null || value === '' ? '' : String(value)
      }
      const left = read(a)
      const right = read(b)
      return sortDirection === 'asc'
        ? left.localeCompare(right, undefined, { sensitivity: 'base' })
        : right.localeCompare(left, undefined, { sensitivity: 'base' })
    }),
  }))
}

export default function UhcAllocations() {
  const { canEdit } = usePageAccess('benefits')
  const [tab, setTab] = useState<TabId>('allocation')
  const [locations, setLocations] = useState<EmployeeLocation[]>([])
  const [loadingLocations, setLoadingLocations] = useState(true)
  const [importingLocations, setImportingLocations] = useState(false)
  const [savingLocationId, setSavingLocationId] = useState<number | null>(null)

  const [draftRows, setDraftRows] = useState<DraftLocationRow[]>([])
  const [groupBy, setGroupBy] = useState<LocationGroupBy>('none')
  const [addingMissingSubscribers, setAddingMissingSubscribers] = useState(false)

  const { sortedData: sortedLocations, requestSort, getSortDirection, sortConfig } =
    useSortableTable(
    locations,
    { key: 'uhc_subscriber_name', direction: 'asc' },
    (item, key) => {
      const value = item[key as keyof EmployeeLocation]
      if (value == null || value === '') return ''
      return value
    }
  )

  const locationSections = useMemo(() => {
    const items = mergeLocationsWithDrafts(sortedLocations, draftRows)
    return buildLocationSections(
      items,
      groupBy,
      sortConfig?.key as string | undefined,
      sortConfig?.direction
    )
  }, [sortedLocations, draftRows, groupBy, sortConfig])

  const hasIncompleteLocations = useMemo(() => {
    if (locations.some(locationRowHasMissingFields)) return true
    return draftRows.some(locationRowHasMissingFields)
  }, [locations, draftRows])

  const loadLocations = useCallback(async () => {
    try {
      setLoadingLocations(true)
      const data = await getEmployeeLocations()
      setLocations(data.items)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load employee locations'))
    } finally {
      setLoadingLocations(false)
    }
  }, [])

  useEffect(() => {
    void loadLocations()
  }, [loadLocations])

  async function handleImportLocations(e: React.ChangeEvent<HTMLInputElement>) {
    if (!canEdit) return
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      setImportingLocations(true)
      const result = await importEmployeeLocationsCsv(file)
      toast.success(`Imported ${result.rows_upserted} employee location rows`)
      setDraftRows([])
      await loadLocations()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to import locations CSV'))
    } finally {
      setImportingLocations(false)
    }
  }

  async function handleUpdateLocation(row: EmployeeLocation, patch: Partial<EmployeeLocationCreate>) {
    if (!canEdit || Object.keys(patch).length === 0) return
    try {
      setSavingLocationId(row.id)
      const updated = await updateEmployeeLocation(row.id, patch)
      setLocations((prev) => prev.map((r) => (r.id === row.id ? updated : r)))
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update location'))
    } finally {
      setSavingLocationId(null)
    }
  }

  function commitLocationField(
    row: EmployeeLocation,
    field: keyof EmployeeLocationCreate,
    rawValue: string
  ) {
    const trimmed = rawValue.trim()
    const current = String(row[field] ?? '').trim()
    if (trimmed === current) return
    if (field === 'uhc_subscriber_name' && !trimmed) return

    const patch: Partial<EmployeeLocationCreate> = {}
    if (field === 'uhc_subscriber_name' || field === 'home_store') {
      patch[field] = trimmed
    } else {
      patch[field] = trimmed || null
    }
    void handleUpdateLocation(row, patch)
  }

  function updateDraftRow(draftId: string, patch: Partial<DraftLocationRow>) {
    setDraftRows((prev) =>
      prev.map((draft) => (draft.draftId === draftId ? { ...draft, ...patch } : draft))
    )
  }

  async function commitDraftRow(draft: DraftLocationRow) {
    if (!canEdit || !draft.uhc_subscriber_name.trim()) return
    try {
      setSavingLocationId(-1)
      const created = await createEmployeeLocation({
        uhc_subscriber_name: draft.uhc_subscriber_name.trim(),
        home_store: draft.home_store,
        homebase_employee_id: draft.homebase_employee_id.trim() || null,
        homebase_first_name: draft.homebase_first_name.trim() || null,
        homebase_last_name: draft.homebase_last_name.trim() || null,
      })
      setDraftRows((prev) => prev.filter((d) => d.draftId !== draft.draftId))
      setLocations((prev) => [...prev, created])
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to add location'))
    } finally {
      setSavingLocationId(null)
    }
  }

  function handleAddDraftBelowLocation(row: EmployeeLocation) {
    if (!canEdit) return
    setDraftRows((prev) => [...prev, emptyDraftRow(row.id, row.home_store)])
  }

  function handleAddDraftBelowDraft(draft: DraftLocationRow) {
    if (!canEdit) return
    const nextDraft = emptyDraftRow(draft.anchorId, draft.home_store)
    setDraftRows((prev) => {
      const index = prev.findIndex((d) => d.draftId === draft.draftId)
      if (index < 0) return [...prev, nextDraft]
      const next = [...prev]
      next.splice(index + 1, 0, nextDraft)
      return next
    })
  }

  function handleRemoveDraft(draftId: string) {
    if (!canEdit) return
    setDraftRows((prev) => prev.filter((d) => d.draftId !== draftId))
  }

  async function handleDeleteLocation(id: number) {
    if (!canEdit) return
    if (!confirm('Delete this employee location row?')) return
    try {
      await deleteEmployeeLocation(id)
      setLocations((prev) => prev.filter((r) => r.id !== id))
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete location'))
    }
  }

  async function handleAddMissingSubscribers(names: string[]) {
    if (!canEdit) return
    const existing = new Set(
      locations.map((row) => row.uhc_subscriber_name.trim().toLowerCase())
    )
    const toAdd = names.filter((name) => !existing.has(name.trim().toLowerCase()))
    if (toAdd.length === 0) {
      setTab('locations')
      toast.success('All listed subscribers are already in Employee locations')
      return
    }

    try {
      setAddingMissingSubscribers(true)
      const created: EmployeeLocation[] = []
      for (const name of toAdd) {
        const row = await createEmployeeLocation({
          uhc_subscriber_name: name.trim(),
          home_store: '',
          homebase_employee_id: null,
          homebase_first_name: null,
          homebase_last_name: null,
        })
        created.push(row)
      }
      setLocations((prev) => [...prev, ...created])
      setTab('locations')
      toast.success(
        `Added ${created.length} subscriber${created.length !== 1 ? 's' : ''} to Employee locations — fill in missing fields highlighted in red`
      )
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to add missing subscribers'))
    } finally {
      setAddingMissingSubscribers(false)
    }
  }

  return (
    <AccountingToolPage
      title="Benefits"
      description="Manage employee store assignments and create benefit reallocation journal entries"
      scrollContent={tab !== 'locations'}
      headerExtra={
        <div className="overflow-x-auto">
          <div
            className="inline-flex min-w-max gap-2 rounded-lg bg-gray-200 p-1"
            role="tablist"
            aria-label="Benefits sections"
          >
            {(
              [
                ['allocation', 'Allocate'],
                ['locations', 'Employee locations'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`relative whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-all ${
                  tab === id
                    ? 'bg-white text-brand-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
                {id === 'locations' && hasIncompleteLocations ? (
                  <span
                    className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500"
                    aria-label="Incomplete employee location rows"
                  />
                ) : null}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {tab === 'locations' ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {canEdit ? (
                <label className="btn btn-secondary text-sm cursor-pointer">
                  <CloudArrowUpIcon className="h-4 w-4" />
                  {importingLocations ? 'Importing…' : 'Import CSV'}
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="sr-only"
                    disabled={importingLocations}
                    onChange={(e) => void handleImportLocations(e)}
                  />
                </label>
              ) : null}
              <span className="text-xs text-gray-500 sm:text-sm">
                {locations.length} mapping{locations.length !== 1 ? 's' : ''}
              </span>
            </div>
            <TableToolbar
              groupBySlot={
                <GroupBySelect
                  value={groupBy}
                  onChange={(v) => setGroupBy(v as LocationGroupBy)}
                  options={LOCATION_GROUP_OPTIONS}
                  aria-label="Group by"
                />
              }
            />
          </div>

          <div className="card flex min-h-0 flex-1 flex-col overflow-hidden p-0">
            <div className="table-scroll-mobile min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain">
              <table className="table-forecast border-separate border-spacing-0 min-w-max">
                <thead>
                  <tr>
                    <SortableHeader
                      label={
                        <HoverHelp label="Subscriber" aria-label="Subscriber help">
                          UHC subscriber name used to match this employee on insurance invoices.
                        </HoverHelp>
                      }
                      sortKey="uhc_subscriber_name"
                      sortDirection={getSortDirection('uhc_subscriber_name')}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      label={
                        <HoverHelp label="Homebase ID" aria-label="Homebase ID help">
                          Homebase employee ID linking this row to payroll and timekeeping.
                        </HoverHelp>
                      }
                      sortKey="homebase_employee_id"
                      sortDirection={getSortDirection('homebase_employee_id')}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      label="First"
                      sortKey="homebase_first_name"
                      sortDirection={getSortDirection('homebase_first_name')}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      label="Last"
                      sortKey="homebase_last_name"
                      sortDirection={getSortDirection('homebase_last_name')}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      label="Home store"
                      sortKey="home_store"
                      sortDirection={getSortDirection('home_store')}
                      onSort={requestSort}
                    />
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody>
                  {loadingLocations && locations.length === 0 && draftRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-sm text-gray-500">
                        <ArrowPathIcon className="mx-auto mb-2 h-4 w-4 animate-spin" />
                        Loading employee locations…
                      </td>
                    </tr>
                  ) : null}
                  {!loadingLocations &&
                  locations.length === 0 &&
                  draftRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-sm text-gray-500">
                        No employee locations yet. Import a CSV to get started.
                      </td>
                    </tr>
                  ) : null}
                  {locationSections.map((section) => (
                    <Fragment key={section.label || 'all'}>
                      {section.label ? (
                        <tr key={`group-${section.label}`} className="bg-gray-50">
                          <td
                            colSpan={6}
                            className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                          >
                            {section.label}
                            <span className="ml-2 font-normal normal-case text-gray-400">
                              ({section.items.length})
                            </span>
                          </td>
                        </tr>
                      ) : null}
                      {section.items.map((item) => {
                        if (item.kind === 'location') {
                          const row = item.row
                          const saving = savingLocationId === row.id
                          return (
                            <tr key={`loc-${row.id}`}>
                              <td>
                                <input
                                  className={inputClassName(
                                    isFieldEmpty(row.uhc_subscriber_name),
                                    'input w-full min-w-[10rem] text-sm'
                                  )}
                                  defaultValue={row.uhc_subscriber_name}
                                  key={`${row.id}-subscriber-${row.updated_at ?? row.uhc_subscriber_name}`}
                                  disabled={!canEdit || saving}
                                  onBlur={(e) =>
                                    commitLocationField(row, 'uhc_subscriber_name', e.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className={inputClassName(
                                    isFieldEmpty(row.homebase_employee_id),
                                    'input w-full min-w-[6rem] text-sm'
                                  )}
                                  defaultValue={row.homebase_employee_id ?? ''}
                                  key={`${row.id}-emp-id-${row.updated_at ?? ''}`}
                                  disabled={!canEdit || saving}
                                  placeholder="ID"
                                  onBlur={(e) =>
                                    commitLocationField(
                                      row,
                                      'homebase_employee_id',
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className={inputClassName(
                                    isFieldEmpty(row.homebase_first_name),
                                    'input w-full min-w-[6rem] text-sm'
                                  )}
                                  defaultValue={row.homebase_first_name ?? ''}
                                  key={`${row.id}-first-${row.updated_at ?? ''}`}
                                  disabled={!canEdit || saving}
                                  placeholder="First"
                                  onBlur={(e) =>
                                    commitLocationField(
                                      row,
                                      'homebase_first_name',
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className={inputClassName(
                                    isFieldEmpty(row.homebase_last_name),
                                    'input w-full min-w-[6rem] text-sm'
                                  )}
                                  defaultValue={row.homebase_last_name ?? ''}
                                  key={`${row.id}-last-${row.updated_at ?? ''}`}
                                  disabled={!canEdit || saving}
                                  placeholder="Last"
                                  onBlur={(e) =>
                                    commitLocationField(row, 'homebase_last_name', e.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <SearchableSelect
                                  options={storeOptions}
                                  value={row.home_store || null}
                                  onChange={(id) =>
                                    id != null && void handleUpdateLocation(row, { home_store: id })
                                  }
                                  disabled={!canEdit || saving}
                                  className="min-w-[9rem]"
                                  error={isFieldEmpty(row.home_store)}
                                  placeholder="Select store"
                                />
                              </td>
                              <td>
                                {canEdit ? (
                                <div className="inline-flex items-center gap-1">
                                  <button
                                    type="button"
                                    className="btn btn-secondary h-7 w-7 p-0 text-base leading-none"
                                    onClick={() => void handleDeleteLocation(row.id)}
                                    aria-label="Remove row"
                                  >
                                    −
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary h-7 w-7 p-0 text-base leading-none"
                                    onClick={() => handleAddDraftBelowLocation(row)}
                                    aria-label="Add row below"
                                  >
                                    +
                                  </button>
                                </div>
                                ) : null}
                              </td>
                            </tr>
                          )
                        }

                        const draft = item.draft
                        const saving = savingLocationId === -1
                        return (
                          <tr key={`draft-${draft.draftId}`} className="bg-gray-50/60">
                            <td>
                              <input
                                className={inputClassName(
                                  isFieldEmpty(draft.uhc_subscriber_name),
                                  'input w-full min-w-[10rem] text-sm'
                                )}
                                value={draft.uhc_subscriber_name}
                                disabled={!canEdit || saving}
                                placeholder="UHC subscriber name"
                                onChange={(e) =>
                                  updateDraftRow(draft.draftId, {
                                    uhc_subscriber_name: e.target.value,
                                  })
                                }
                                onBlur={() => void commitDraftRow(draft)}
                              />
                            </td>
                            <td>
                              <input
                                className={inputClassName(
                                  isFieldEmpty(draft.homebase_employee_id),
                                  'input w-full min-w-[6rem] text-sm'
                                )}
                                value={draft.homebase_employee_id}
                                disabled={!canEdit || saving}
                                placeholder="ID"
                                onChange={(e) =>
                                  updateDraftRow(draft.draftId, {
                                    homebase_employee_id: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                className={inputClassName(
                                  isFieldEmpty(draft.homebase_first_name),
                                  'input w-full min-w-[6rem] text-sm'
                                )}
                                value={draft.homebase_first_name}
                                disabled={!canEdit || saving}
                                placeholder="First"
                                onChange={(e) =>
                                  updateDraftRow(draft.draftId, {
                                    homebase_first_name: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                className={inputClassName(
                                  isFieldEmpty(draft.homebase_last_name),
                                  'input w-full min-w-[6rem] text-sm'
                                )}
                                value={draft.homebase_last_name}
                                disabled={!canEdit || saving}
                                placeholder="Last"
                                onChange={(e) =>
                                  updateDraftRow(draft.draftId, {
                                    homebase_last_name: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <SearchableSelect
                                options={storeOptions}
                                value={draft.home_store || null}
                                onChange={(id) =>
                                  updateDraftRow(draft.draftId, { home_store: id ?? '' })
                                }
                                disabled={!canEdit || saving}
                                className="min-w-[9rem]"
                                error={isFieldEmpty(draft.home_store)}
                                placeholder="Select store"
                              />
                            </td>
                            <td>
                              {canEdit ? (
                              <div className="inline-flex items-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-secondary h-7 w-7 p-0 text-base leading-none"
                                  onClick={() => handleRemoveDraft(draft.draftId)}
                                  aria-label="Remove row"
                                >
                                  −
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary h-7 w-7 p-0 text-base leading-none"
                                  onClick={() => handleAddDraftBelowDraft(draft)}
                                  aria-label="Add row below"
                                >
                                  +
                                </button>
                              </div>
                              ) : null}
                            </td>
                          </tr>
                        )
                      })}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <UhcAllocate
          canEdit={canEdit}
          onAddMissing={canEdit ? handleAddMissingSubscribers : undefined}
          addingMissing={addingMissingSubscribers}
        />
      )}
    </AccountingToolPage>
  )
}
